import React, { FC, PropsWithChildren, useMemo } from 'react';
import { Flex } from 'antd';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  CategoryFormDataType,
  CategoryRelationFormDataType,
  ClassifierFormDataType,
  CloneFormTableRowButton,
  CreateFormTableRowButton,
  defaultCategoryRelationFormData,
  DeleteFormTableRowButton,
  FormField,
  FormTableSequenceNumberField,
  getDatePeriodString,
  getFormTableFieldName,
  TanstackFormTable,
  getValidPeriodDisplayValueWithFormValueAttached,
  RequiredText,
} from '@atlas-frontend/sdk';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useClassifierFormLabelTranslation } from '../form/useClassifierFormLabelTranslation';
import { CategoryRelationSelect } from './input/CategoryRelationSelect';
import { CategoryRelationSelectPreview } from './input/CategoryRelationSelectPreview';
import { SubCategoryTypeInputPreview } from './input/SubCategoryTypeInputPreview';
import { CategoryValidPeriodPicker } from './input/CategoryValidPeriodPicker';
import { CategoryRelationsModal } from './CategoryRelationsModal';

const columnHelper = createColumnHelper<CategoryRelationFormDataType>();

export interface CategoryRelationsModalProps extends PropsWithChildren {
  name: 'categories' | 'categoryGroups' | 'combinedGroups';
  index: number;
  onClose: () => void;
  classifierValidPeriod: [string | null, string | null];
}

export const CategoryRelationsFormModal: FC<CategoryRelationsModalProps> = (
  props,
) => {
  const { name, index, onClose, classifierValidPeriod } = props;
  const { t: tLabel } = useClassifierFormLabelTranslation();
  const relationsName = `${name}.${index}.categoryRelations` as const;
  const categoryData = useWatch({
    name: `${name}.${index}`,
  }) as CategoryFormDataType;
  const { getValues } = useFormContext<ClassifierFormDataType>();

  const isCombinedGroups = name === 'combinedGroups';

  const columns = useMemo(() => {
    const columns: ColumnDef<CategoryRelationFormDataType, any>[] = [
      columnHelper.accessor('sequenceNumber', {
        header: tLabel(`${name}.categoryRelations.sequenceNumber.label`),
        cell: (props) => <FormTableSequenceNumberField {...props} />,
        meta: {
          width: 100,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor(
        (rowData) => `${rowData.subCategory} - ${rowData.subCategoryName}`,
        {
          id: 'subCategory',
          header: () => (
            <RequiredText>
              {tLabel(`${name}.categoryRelations.subCategory.label`)}
            </RequiredText>
          ),
          cell: (props) => {
            const name = `${relationsName}.${props.row.index}.subCategory`;
            return (
              <FormField
                name={name}
                preview={{
                  render: (value) => (
                    <CategoryRelationSelectPreview
                      selectedValue={value}
                      isCombinedGroups={isCombinedGroups}
                    />
                  ),
                }}
              >
                <CategoryRelationSelect
                  name={name}
                  isCombinedGroups={isCombinedGroups}
                />
              </FormField>
            );
          },
          meta: {
            width: 180,
          },
        },
      ),
    ];

    if (isCombinedGroups) {
      columns.push(
        columnHelper.accessor('subCategoryType', {
          header: tLabel(`${name}.categoryRelations.subCategoryType.label`),
          cell: (props) => (
            <FormField
              name={`${relationsName}.${props.row.index}.subCategoryType`}
              preview={{
                previewOnly: true,
                render: () => (
                  <SubCategoryTypeInputPreview
                    isCombinedGroups={isCombinedGroups}
                    parentName={relationsName}
                    formDataIndex={props.row.index}
                  />
                ),
              }}
            />
          ),
          meta: {
            width: 100,
          },
        }),
      );
    }

    columns.push(
      columnHelper.accessor(
        (rowData, index) =>
          getValidPeriodDisplayValueWithFormValueAttached(
            getValues(`${relationsName}.${index}.validPeriod`),
          ),
        {
          id: 'validPeriod',
          header: tLabel(`${name}.categoryRelations.validPeriod.label`),
          cell: (props) => {
            const name = getFormTableFieldName(props);
            return (
              <FormField
                name={name}
                preview={{
                  render: (value) =>
                    getDatePeriodString(value?.[0], value?.[1]),
                }}
              >
                <CategoryValidPeriodPicker
                  name={name}
                  autoFocus
                  classifierValidPeriod={classifierValidPeriod}
                />
              </FormField>
            );
          },
          meta: {
            width: 266,
          },
          filterFn: 'validPeriod',
        },
      ),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton
              defaultRowValues={{
                ...defaultCategoryRelationFormData,
                validPeriod: [classifierValidPeriod[0] || null, null],
              }}
              autoFocusField="subCategory"
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <CloneFormTableRowButton
              context={props}
              autoFocusField="subCategory"
            />
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    );

    return columns;
  }, [
    name,
    relationsName,
    tLabel,
    isCombinedGroups,
    classifierValidPeriod,
    getValues,
  ]);

  return (
    <CategoryRelationsModal
      name={name}
      categoryData={categoryData}
      onClose={onClose}
    >
      <Flex vertical gap="middle" align="end">
        <TanstackFormTable
          name={`${name}.${index}.categoryRelations`}
          columns={columns}
          sticky
          virtual
          scrollContainerSelector="#category-relations-modal-content"
        />
      </Flex>
    </CategoryRelationsModal>
  );
};
